import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaSave } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../../api/httpClient";
import { API_PATHS } from "../../api/apiPaths";
import { useAppTheme } from "../../context/AppThemeContext";

const DEFINITIONS = {
  students: {
    singular: "student", title: "Student admission", list: API_PATHS.tuition.students,
    byId: API_PATHS.tuition.studentById,
    fields: [
      ["name", "Student name", "text", true], ["rollNo", "Roll number", "text", true],
      ["email", "Email", "email"], ["mobile", "Mobile", "tel"], ["batch", "Batch", "text", true],
      ["grade", "Grade / level"], ["subjects", "Subjects (comma-separated)", "text", true],
      ["parentName", "Parent name"], ["parentMobile", "Parent mobile", "tel"],
      ["parentRelation", "Relationship"], ["totalFee", "Total fee", "number"],
      ["paidFee", "Paid fee", "number"], ["dueDate", "Fee due date", "date"],
    ],
  },
  teachers: {
    singular: "teacher", title: "Faculty profile", list: API_PATHS.tuition.teachers,
    byId: API_PATHS.tuition.teacherById,
    fields: [
      ["name", "Teacher name", "text", true], ["email", "Email", "email", true],
      ["phone", "Phone", "tel"], ["specialization", "Specializations (comma-separated)", "text", true],
      ["assignedBatches", "Assigned batches (comma-separated)"], ["monthlySalary", "Monthly salary", "number"],
      ["qualification", "Qualification"],
    ],
  },
  classes: {
    singular: "class", title: "Online class", list: API_PATHS.tuition.onlineClasses,
    byId: API_PATHS.tuition.onlineClassById,
    fields: [
      ["topic", "Lecture topic", "text", true], ["subject", "Subject", "text", true],
      ["batch", "Batch", "text", true], ["teacherName", "Teacher name", "text", true],
      ["scheduledDate", "Date", "date", true], ["startTime", "Start time", "time", true],
      ["endTime", "End time", "time", true],
    ],
  },
  assessments: {
    singular: "assessment", title: "Assessment", list: API_PATHS.tuition.assessments,
    byId: API_PATHS.tuition.assessmentById,
    fields: [
      ["title", "Assessment title", "text", true], ["subject", "Subject", "text", true],
      ["batch", "Batch", "text", true], ["maxMarks", "Maximum marks", "number", true],
      ["date", "Assessment date", "date", true],
    ],
  },
};

const unwrap = (response) => response?.data?.data ?? response?.data ?? {};
const normalizeValue = (value) => Array.isArray(value) ? value.join(", ") : (value ?? "");

export default function TuitionEntityFormPage({ entityName = "" }) {
  const { entity, entityId } = useParams();
  const resolvedEntity = entityName || entity;
  const navigate = useNavigate();
  const { isDarkMode } = useAppTheme();
  const definition = DEFINITIONS[resolvedEntity];
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(Boolean(entityId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isEditing = Boolean(entityId);
  const pageTitle = useMemo(() => `${isEditing ? "Update" : "Add"} ${definition?.title || "record"}`, [definition, isEditing]);

  useEffect(() => {
    if (!definition || !entityId) return;
    let active = true;
    setLoading(true);
    api.get(definition.byId(entityId))
      .then((response) => {
        if (!active) return;
        const payload = unwrap(response);
        const record = payload[definition.singular] || payload;
        setForm(Object.fromEntries(definition.fields.map(([key]) => [key, normalizeValue(record?.[key])] )));
      })
      .catch((requestError) => active && setError(requestError?.message || `Unable to load this ${definition.singular}.`))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [definition, entityId]);

  if (!definition) {
    return <div className="mx-auto max-w-3xl p-6"><p role="alert">This tuition form does not exist.</p></div>;
  }

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    const missing = definition.fields.find(([key, , , required]) => required && !String(form[key] || "").trim());
    if (missing) { setError(`${missing[1]} is required.`); return; }
    const payload = { ...form };
    ["subjects", "specialization", "assignedBatches"].forEach((key) => {
      if (key in payload) payload[key] = String(payload[key] || "").split(",").map((item) => item.trim()).filter(Boolean);
    });
    ["totalFee", "paidFee", "monthlySalary", "maxMarks"].forEach((key) => {
      if (key in payload && payload[key] !== "") payload[key] = Number(payload[key]);
    });
    setSaving(true);
    try {
      if (isEditing) await api.put(definition.byId(entityId), payload);
      else await api.post(definition.list(), payload);
      toast.success(`${definition.title} ${isEditing ? "updated" : "created"} successfully.`);
      navigate("/tuition/dashboard", { replace: true, state: { tuitionTab: resolvedEntity } });
    } catch (requestError) {
      setError(requestError?.message || `Unable to save this ${definition.singular}.`);
    } finally { setSaving(false); }
  };

  return (
    <div className={`min-h-full px-3 py-5 sm:px-6 lg:px-8 ${isDarkMode ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`}>
      <div className="mx-auto max-w-4xl">
        <button type="button" onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-sm font-bold hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800">
          <FaArrowLeft /> Back
        </button>
        <section className={`rounded-3xl border p-5 shadow-sm sm:p-8 ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <h1 className="text-2xl font-black">{pageTitle}</h1>
          <p className="mt-1 text-sm text-slate-500">Fields are saved to the active tuition workspace.</p>
          {loading ? <p className="mt-8 text-sm" role="status">Loading record…</p> : (
            <form onSubmit={submit} className="mt-6">
              {error && <div role="alert" className="mb-5 rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm font-semibold text-rose-700">{error}</div>}
              <div className="grid gap-5 sm:grid-cols-2">
                {definition.fields.map(([key, label, type = "text", required = false]) => (
                  <label key={key} className="block text-sm font-bold">
                    <span>{label}{required ? " *" : ""}</span>
                    <input type={type} required={required} min={type === "number" ? "0" : undefined} value={form[key] ?? ""} onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))} className={`mt-2 w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 ${isDarkMode ? "border-slate-700 bg-slate-950" : "border-slate-300 bg-white"}`} />
                  </label>
                ))}
              </div>
              <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button type="button" onClick={() => navigate(-1)} className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-bold dark:border-slate-700">Cancel</button>
                <button type="submit" disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-sky-700 disabled:opacity-60"><FaSave /> {saving ? "Saving…" : "Save"}</button>
              </div>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}
